import { useState, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

export type PickerItem = { id: string; label: string; icon?: ReactNode; hint?: string; keywords?: string };

/**
 * Searchable property popover (status, priority, assignee, labels…).
 * Controlled `open` lets keyboard shortcuts pop it without a click.
 */
export function Picker({
  trigger,
  items,
  value,
  onSelect,
  multi = false,
  open,
  onOpenChange,
  placeholder,
  align = "start",
  footer,
}: {
  trigger: ReactNode;
  items: PickerItem[];
  value: string | string[] | null | undefined;
  onSelect: (id: string) => void;
  multi?: boolean;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  placeholder?: string;
  align?: "start" | "end" | "center";
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const [internal, setInternal] = useState(false);
  const isOpen = open ?? internal;
  const setOpen = onOpenChange ?? setInternal;
  const selected = new Set(Array.isArray(value) ? value : value ? [value] : []);

  return (
    <Popover.Root open={isOpen} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={4}
          className="z-50 w-[260px] overflow-hidden rounded-lg border border-border bg-elevated shadow-xl"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Command loop>
            <Command.Input autoFocus placeholder={placeholder ?? t("common.search")} className="h-9 w-full border-b border-border bg-transparent px-3 text-[13px] outline-none placeholder:text-faint" />
            <Command.List className="max-h-[300px] overflow-y-auto p-1">
              <Command.Empty className="px-3 py-4 text-center text-[12px] text-muted">{t("common.noResults")}</Command.Empty>
              {items.map((it) => (
                <Command.Item
                  key={it.id}
                  value={`${it.label} ${it.keywords ?? ""}`}
                  onSelect={() => {
                    onSelect(it.id);
                    if (!multi) setOpen(false);
                  }}
                  className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] data-[selected=true]:bg-hover"
                >
                  <span className="flex w-4 items-center justify-center text-muted">{it.icon}</span>
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.hint && <span className="text-[11px] text-faint">{it.hint}</span>}
                  {selected.has(it.id) && <Check className="h-3.5 w-3.5 text-accent" />}
                </Command.Item>
              ))}
            </Command.List>
            {footer && <div className="border-t border-border p-1">{footer}</div>}
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Small button used as picker trigger inside rows and property panels. */
export function PropButton({ children, className = "", title, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      title={title}
      className={`inline-flex h-6 max-w-full items-center gap-1.5 rounded-md border border-transparent px-1.5 text-[12.5px] text-muted hover:border-border-strong hover:bg-hover hover:text-fg ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
